import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import {
	EventType,
	IChatCommandWidgetFormData,
	IChatCommandWidgetMessage,
	IChatEventFormMessage,
	IChatMessagesOut,
	MessageType,
} from '@enuan/chat__utils';
import { CollapsibleForm } from '../../form/CollapsibleForm';
import { useMessageContext } from '../../message/message.context';
import { useBlockMessageClassNames } from 'src/hooks/core/messages.hooks';
import { useSendMessage } from 'src/hooks/core/messages.hooks';
import { useDispatch } from 'react-redux';
import { messagesSlice } from '../../../reduxStore';
import { useLogError } from '../../ErrorBoundary/useLogError';
import { BaseFormMessage } from './BaseFormMessage';
import { getFormLabels, getMessageFormPrefix } from '../../../utils';
import { IFormProps } from '../../form';
import type { IOptionalMessageProps } from '../../UI/Message/Message';

type IFormMessageContent =
	IChatCommandWidgetMessage<IChatCommandWidgetFormData>;

export const FormMessage = React.forwardRef<
	HTMLElement,
	{ className?: string } & IOptionalMessageProps
>(function FormMessage({ className, ...props }, ref) {
	const dispatch = useDispatch();
	const { message } = useMessageContext<IFormMessageContent>();
	const messageClassName = useBlockMessageClassNames();
	const logError = useLogError('FormMessage', { message });
	const {
		client_id,
		_meta: { sent, disabled } = {},
		_data: { parameters },
		metadata: { id },
	} = message;
	const { data: formConfig } = parameters;

	const setReadOnly = useCallback(
		(readOnly: boolean) => {
			if (!formConfig.reusable) {
				dispatch(
					messagesSlice.actions.toggleDisabled({
						client_id,
						disabled: readOnly,
					}),
				);
			}
		},
		[formConfig.reusable, dispatch, client_id],
	);

	const sendMessage = useSendMessage();

	const onSubmitAction = useCallback(
		(values: Record<string, any>) => {
			const formLabels = getFormLabels(formConfig?.items, values);

			const event: IChatMessagesOut<IChatEventFormMessage> = {
				_type: MessageType.EVENT,
				_data: {
					name: EventType.POSTBACK,
					parameters: {
						payload: values,
						metadata: {
							command_id: id,
							labels: { form: formLabels, header: formConfig.header },
						},
					},
				},
			};

			setReadOnly(true);
			sendMessage(event)
				.then((result) => {
					if (result) {
						if (formConfig.reusable) {
							dispatch(
								messagesSlice.actions.setForm({
									client_id,
									form: undefined,
								}),
							);
						}
					} else {
						setReadOnly(false);
					}
				})
				.catch((error) => logError('submit', { error, payload: { event } }));
			return true;
		},
		[
			dispatch,
			formConfig.header,
			formConfig.items,
			formConfig.reusable,
			id,
			client_id,
			sendMessage,
			logError,
			setReadOnly,
		],
	);

	const formProps = useMemo<IFormProps>(
		() => ({
			prefix: getMessageFormPrefix(client_id),
			readOnly: disabled,
			onSubmitAction: onSubmitAction,
			formConfig: formConfig,
		}),
		[client_id, disabled, onSubmitAction, formConfig],
	);

	return (
		<BaseFormMessage
			{...props}
			ref={ref}
			message={message}
			className={classNames(className, messageClassName, 'message--form')}
			customHeader={null}
			customAvatar={null}
		>
			<CollapsibleForm
				toggable={sent === true}
				title={formConfig.header.title || ''}
				formProps={formProps}
			/>
		</BaseFormMessage>
	);
});
